// Right-column contextual help for the redesigned NewTriggerDialog.
// Reads from a per-action lookup of {summary, examples, tips}. The
// summary mirrors ACTION_META.blurb but trimmed; examples are concrete
// payload→result transforms; tips note the most common mistakes.
//
// The panel is always visible alongside the form so operators don't
// have to recall what an action does mid-edit. No long prose blocks
// inline in the form — that's the point of the split.

import type { TriggerActionType } from '../triggers/ActionPicker';

interface ActionHelp {
  summary: string;
  examples?: Array<{ in: string; out: string; note?: string }>;
  tips?: string[];
}

const HELP: Record<TriggerActionType, ActionHelp> = {
  forward_webhook: {
    summary: 'POST the payload to an external URL with HMAC-signed headers. Retries 3 times. SSRF-blocked + self-loop guard.',
    examples: [
      { in: '{ deal_id: "X", amount: 100 }', out: 'POST → external, X-Datahub-Signature: sha256=…' },
    ],
    tips: [
      '@SECRET_NAME placeholders in the URL resolve from your vault.',
      'For bulk uploads (?bulk=true), use forward_webhook_batched instead — per-event POSTs are skipped in bulk mode.',
    ],
  },
  forward_webhook_batched: {
    summary: 'Bulk-mode-friendly: ?bulk=true uploads chunk events into batches per outbound POST. A 100k-row CSV becomes ~1000 outbound POSTs instead of 100k.',
    examples: [
      { in: '5,000-event CSV upload + batch_size=100', out: '50 outbound POSTs, each with {events:[100], batch_index, batch_total}' },
    ],
    tips: [
      'Body shape: {events:[…], batch_index, batch_total}.',
      'Same HMAC signing + SSRF guards as forward_webhook.',
      'Per-event mode (non-bulk ingest) sends 1 event in the batched envelope so receivers parse one shape.',
    ],
  },
  promote_to_table: {
    summary: 'Create a BigQuery view over the source\'s GCS objects. Zero fixed storage cost — pay only on query scan. New events arriving in the source are auto-included on the next query.',
    examples: [
      { in: '{ deal_id: "X", amount: 100 }', out: 'queryable in BQ as datahub.<table_name> view, every event for the source visible' },
    ],
    tips: [
      'The view exposes EVERY event for the source — filter at query time via WHERE.',
      'No active storage cost. Pay $5/TB scanned.',
      'For upserts/CDC, use SQL: ROW_NUMBER OVER (PARTITION BY business_key ORDER BY received_at DESC).',
    ],
  },
  validate_required: {
    summary: 'Data-quality gate. Asserts every listed field is present. Missing → trigger run logs as failure AND quarantines the event so other triggers (forward_webhook, promote_to_table) skip it.',
    examples: [
      { in: '{ deal_id: "X" } with required ["deal_id","amount"]', out: 'failure · quarantines event · downstream skipped' },
    ],
    tips: ['Empty string also counts as missing.', 'Use a saved View as scope to narrow validation to a subset.'],
  },
  validate_dedupe: {
    summary: 'Sliding-window dedupe on a payload key. Second event with the same key inside the window quarantines.',
    examples: [
      { in: '{ order_id: "1234" } twice in 1 minute, window=3600', out: 'second one fails · quarantined' },
    ],
    tips: ['window_sec max 7 days (604800).', 'Default 3600 (1 hour).'],
  },
  enrich_fetch: {
    summary: 'GET an external URL using payload[id_path] for {id} substitution; merge JSON response into payload at merge_path. Emits a derivative event.',
    examples: [
      { in: 'url=https://crm.api/contacts/{id} · id_path=customer_id · merge_path=customer', out: '{ ...original, customer: { ...response } }' },
    ],
    tips: ['SSRF-blocked (no cloud-metadata, RFC1918, self-loop).', '@SECRET_NAME placeholders in the URL resolve from vault.'],
  },
};

export function TriggerHelpPanel({ type }: { type: TriggerActionType }): React.ReactElement {
  const help = HELP[type];
  return (
    <aside style={{
      padding: '14px 16px',
      background: 'var(--bg-sunken)',
      borderLeft: '1px solid var(--line)',
      display: 'flex', flexDirection: 'column', gap: 14,
      fontSize: 12.5, color: 'var(--ink-2)', lineHeight: 1.5,
    }}>
      <div>
        <div style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginBottom: 6 }}>
          About this action
        </div>
        <div style={{ color: 'var(--ink)' }}>{help.summary}</div>
      </div>

      {help.examples && help.examples.length > 0 && (
        <div>
          <div style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginBottom: 6 }}>
            Example
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {help.examples.map((ex, i) => (
              <div key={i} style={{
                padding: 8,
                background: 'var(--bg)',
                border: '1px solid var(--line)',
                borderRadius: 4,
              }}>
                <code style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--ink-2)', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  in:  {ex.in}
                </code>
                <code style={{ display: 'block', fontFamily: 'var(--mono)', fontSize: 11, color: 'oklch(0.55 0.14 145)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginTop: 4 }}>
                  out: {ex.out}
                </code>
                {ex.note && (
                  <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 4 }}>{ex.note}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {help.tips && help.tips.length > 0 && (
        <div>
          <div style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginBottom: 6 }}>
            Tips
          </div>
          <ul style={{ margin: 0, padding: '0 0 0 18px', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {help.tips.map((t, i) => <li key={i}>{t}</li>)}
          </ul>
        </div>
      )}
    </aside>
  );
}
